import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Video,
  Clock,
  MapPin,
  Users,
  Loader2,
  RefreshCw,
  Calendar,
  X,
} from 'lucide-react';
import { scheduleApi, eventsApi, meetingsApi } from '../../../lib/api';
import { useAuth } from '../../../app/providers/AuthProvider';
import { CreateEventModal } from './CreateEventModal';
import { useNavigate } from 'react-router-dom';

type ViewMode = 'daily' | 'weekly' | 'monthly';
type FilterType = 'all' | 'meetings' | 'events' | 'classes' | 'lessons';

interface ScheduleItem {
  id: string;
  source: 'event' | 'meeting';
  type: string;
  title: string;
  description: string | null;
  start: string;
  end: string | null;
  all_day: boolean;
  location: string | null;
  color: string;
  course: { id: string; name: string } | null;
  creator: { id: string; name: string } | null;
  has_video_meeting: boolean;
  meeting: {
    id: string;
    room_name: string;
    status: string;
    recording_enabled: boolean;
    auto_recording: boolean;
  } | null;
}

const hours = Array.from({ length: 14 }, (_, i) => i + 8); // 8am to 9pm

const typeColors: Record<string, string> = {
  lesson: 'bg-orange-100 border-orange-400 text-orange-800',
  class: 'bg-orange-100 border-orange-400 text-orange-800',
  meeting: 'bg-blue-100 border-blue-400 text-blue-800',
  event: 'bg-purple-100 border-purple-400 text-purple-800',
  office_hours: 'bg-cyan-100 border-cyan-400 text-cyan-800',
  break: 'bg-yellow-100 border-yellow-400 text-yellow-800',
  holiday: 'bg-red-100 border-red-400 text-red-800',
};

export function SchoolScheduleTab() {
  const { currentSchool } = useAuth();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('weekly');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [schedule, setSchedule] = useState<ScheduleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const filters: { id: FilterType; label: string }[] = [
    { id: 'all', label: 'View All' },
    { id: 'lessons', label: 'Lessons' },
    { id: 'meetings', label: 'Meetings' },
    { id: 'events', label: 'Events' },
  ];

  // Calculate date range based on view mode
  const getDateRange = useCallback(() => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);

    if (viewMode === 'daily') {
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    } else if (viewMode === 'weekly') {
      const day = start.getDay();
      start.setDate(start.getDate() - day);
      start.setHours(0, 0, 0, 0);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else {
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
      end.setHours(23, 59, 59, 999);
    }

    return { start, end };
  }, [currentDate, viewMode]);

  // Fetch schedule data
  const fetchSchedule = useCallback(async () => {
    if (!currentSchool) return;

    setLoading(true);
    setError(null);

    try {
      const { start, end } = getDateRange();
      const response = await scheduleApi.get({
        start: start.toISOString().split('T')[0],
        end: end.toISOString().split('T')[0],
        type: activeFilter !== 'all' ? activeFilter.replace('lessons', 'lesson') : undefined,
      });

      setSchedule(response.schedule || []);
    } catch (err: any) {
      console.error('Failed to fetch schedule:', err);
      setError(err.message || 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [currentSchool, getDateRange, activeFilter]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule, refreshKey]);

  const refresh = () => setRefreshKey(k => k + 1);

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (viewMode === 'daily') {
        newDate.setDate(newDate.getDate() + (direction === 'prev' ? -1 : 1));
      } else if (viewMode === 'weekly') {
        newDate.setDate(newDate.getDate() + (direction === 'prev' ? -7 : 7));
      } else {
        newDate.setMonth(newDate.getMonth() + (direction === 'prev' ? -1 : 1));
      }
      return newDate;
    });
  };

  // Get week days for weekly view
  const getWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(startOfWeek);
      day.setDate(day.getDate() + i);
      return day;
    });
  };

  // Get days in month for monthly view
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days: (number | null)[] = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const weekDays = getWeekDays();
  const monthDays = getDaysInMonth();

  // Get events for a specific date
  const getEventsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return schedule.filter((item) => {
      const itemDate = new Date(item.start).toISOString().split('T')[0];
      return itemDate === dateStr;
    });
  };

  // Get events for a specific day of month
  const getEventsForDay = (day: number) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return getEventsForDate(date);
  };

  const getEventTopPosition = (startTime: string) => {
    const date = new Date(startTime);
    const hour = date.getHours();
    const minute = date.getMinutes();
    return ((hour - 8) * 60 + minute) * (48 / 60);
  };

  const getEventHeight = (startTime: string, endTime: string | null) => {
    if (!endTime) return 48; // Default 1 hour
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
    return Math.max(durationMinutes * (48 / 60), 24);
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const getEventColor = (item: ScheduleItem) => {
    return typeColors[item.type] || 'bg-gray-100 border-gray-400 text-gray-800';
  };

  const handleJoinMeeting = async (meetingId: string) => {
    navigate(`/meetings/${meetingId}`);
  };

  const handleStartInstantMeeting = async () => {
    if (!currentSchool) return;

    try {
      const now = new Date();
      const endTime = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour from now

      const response = await meetingsApi.create({
        school_id: currentSchool.id,
        title: `Instant Meeting - ${now.toLocaleString()}`,
        meeting_type: 'meeting',
        scheduled_start: now.toISOString(),
        scheduled_end: endTime.toISOString(),
      });

      if (response.meeting) {
        // Start the meeting
        await meetingsApi.start(response.meeting.id);
        navigate(`/meetings/${response.meeting.id}`);
      }
    } catch (err) {
      console.error('Failed to start instant meeting:', err);
    }
  };

  const handleEventCreated = () => {
    setShowCreateModal(false);
    refresh();
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  if (loading && schedule.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Date Navigation */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigateDate('prev')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 min-w-[200px] text-center">
            {viewMode === 'daily' && (
              <>
                {dayNames[currentDate.getDay()]}, {monthNames[currentDate.getMonth()]} {currentDate.getDate()}, {currentDate.getFullYear()}
              </>
            )}
            {viewMode === 'weekly' && (
              <>
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </>
            )}
            {viewMode === 'monthly' && (
              <>
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </>
            )}
          </h2>
          <button
            onClick={() => navigateDate('next')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                activeFilter === filter.id
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* View Mode & Actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={refresh}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value as ViewMode)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>

          <button
            onClick={goToToday}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Today
          </button>

          <button
            onClick={handleStartInstantMeeting}
            className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
          >
            <Video className="w-4 h-4" />
            Start Meeting
          </button>

          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
          >
            <Plus className="w-4 h-4" />
            New Event
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Calendar */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {viewMode === 'weekly' && (
          <div className="flex">
            {/* Time Column */}
            <div className="w-16 flex-shrink-0 border-r border-gray-200">
              <div className="h-12 border-b border-gray-200" />
              {hours.map((hour) => (
                <div
                  key={hour}
                  className="h-12 border-b border-gray-100 px-2 text-xs text-gray-500 flex items-start pt-1"
                >
                  {hour}:00
                </div>
              ))}
            </div>

            {/* Days */}
            <div className="flex-1 grid grid-cols-7">
              {/* Day Headers */}
              {weekDays.map((day, index) => (
                <div
                  key={index}
                  className={`border-b border-r border-gray-200 p-2 text-center ${
                    isToday(day) ? 'bg-teal-50' : ''
                  }`}
                >
                  <div className="text-xs text-gray-500">{dayNames[day.getDay()]}</div>
                  <div className={`text-lg font-semibold ${
                    isToday(day) ? 'text-teal-600' : 'text-gray-900'
                  }`}>
                    {day.getDate()}
                  </div>
                </div>
              ))}

              {/* Time Grid */}
              {weekDays.map((day, dayIndex) => (
                <div key={dayIndex} className={`relative border-r border-gray-200 ${
                  isToday(day) ? 'bg-teal-50/30' : ''
                }`}>
                  {hours.map((hour) => (
                    <div key={hour} className="h-12 border-b border-gray-100" />
                  ))}

                  {/* Events */}
                  {getEventsForDate(day).map((item) => (
                    <div
                      key={item.id}
                      onClick={() => item.meeting && handleJoinMeeting(item.meeting.id)}
                      className={`absolute left-1 right-1 rounded-lg border-l-4 p-1 text-xs overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${getEventColor(item)}`}
                      style={{
                        top: `${getEventTopPosition(item.start) + 48}px`,
                        height: `${getEventHeight(item.start, item.end)}px`,
                      }}
                    >
                      <div className="font-medium truncate flex items-center gap-1">
                        {item.has_video_meeting && <Video className="w-3 h-3" />}
                        {item.title}
                      </div>
                      <div className="text-xs opacity-75">
                        {formatTime(item.start)} - {item.end ? formatTime(item.end) : ''}
                      </div>
                      {item.meeting?.status === 'live' && (
                        <span className="inline-flex items-center gap-1 px-1 py-0.5 bg-red-500 text-white text-xs rounded mt-1">
                          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                          LIVE
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {viewMode === 'monthly' && (
          <div>
            {/* Day Headers */}
            <div className="grid grid-cols-7 border-b border-gray-200">
              {dayNames.map((day) => (
                <div key={day} className="p-3 text-center text-sm font-medium text-gray-500">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7">
              {monthDays.map((day, index) => {
                const date = day ? new Date(currentDate.getFullYear(), currentDate.getMonth(), day) : null;
                const dayEvents = day ? getEventsForDay(day) : [];

                return (
                  <div
                    key={index}
                    className={`min-h-[100px] p-2 border-b border-r border-gray-100 ${
                      day === null ? 'bg-gray-50' : ''
                    } ${date && isToday(date) ? 'bg-teal-50' : ''}`}
                  >
                    {day && (
                      <>
                        <div className={`text-sm font-medium mb-1 ${
                          date && isToday(date) ? 'text-teal-600' : 'text-gray-900'
                        }`}>
                          {day}
                        </div>
                        <div className="space-y-1">
                          {dayEvents.slice(0, 3).map((item) => (
                            <div
                              key={item.id}
                              onClick={() => item.meeting && handleJoinMeeting(item.meeting.id)}
                              className={`text-xs p-1 rounded truncate cursor-pointer hover:shadow ${getEventColor(item)}`}
                            >
                              {item.has_video_meeting && <Video className="w-3 h-3 inline mr-1" />}
                              {item.title}
                              {item.meeting?.status === 'live' && (
                                <span className="ml-1 text-red-500 font-bold">LIVE</span>
                              )}
                            </div>
                          ))}
                          {dayEvents.length > 3 && (
                            <div className="text-xs text-gray-500">
                              +{dayEvents.length - 3} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {viewMode === 'daily' && (
          <div className="flex">
            {/* Time Column */}
            <div className="w-20 flex-shrink-0 border-r border-gray-200">
              <div className="h-16 border-b border-gray-200 p-3">
                <div className="text-2xl font-bold text-gray-900">{currentDate.getDate()}</div>
                <div className="text-sm text-gray-500">{dayNames[currentDate.getDay()]}</div>
              </div>
              {hours.map((hour) => (
                <div
                  key={hour}
                  className="h-12 border-b border-gray-100 px-3 text-xs text-gray-500 flex items-start pt-1"
                >
                  {hour}:00
                </div>
              ))}
            </div>

            {/* Day Content */}
            <div className="flex-1 relative">
              <div className="h-16 border-b border-gray-200" />
              {hours.map((hour) => (
                <div key={hour} className="h-12 border-b border-gray-100" />
              ))}

              {/* Events */}
              {getEventsForDate(currentDate).map((item) => (
                <div
                  key={item.id}
                  onClick={() => item.meeting && handleJoinMeeting(item.meeting.id)}
                  className={`absolute left-2 right-2 rounded-lg border-l-4 p-2 cursor-pointer hover:shadow-md transition-shadow ${getEventColor(item)}`}
                  style={{
                    top: `${getEventTopPosition(item.start) + 64}px`,
                    height: `${getEventHeight(item.start, item.end)}px`,
                  }}
                >
                  <div className="font-medium flex items-center gap-2">
                    {item.has_video_meeting && <Video className="w-4 h-4" />}
                    {item.title}
                    {item.meeting?.status === 'live' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded">
                        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                        LIVE
                      </span>
                    )}
                  </div>
                  <div className="text-sm opacity-75 flex items-center gap-3 mt-1">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTime(item.start)} - {item.end ? formatTime(item.end) : ''}
                    </span>
                    {item.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {item.location}
                      </span>
                    )}
                  </div>
                  {item.course && (
                    <div className="text-sm opacity-75 mt-1">{item.course.name}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Upcoming Live Meetings */}
      {schedule.filter(s => s.meeting?.status === 'live').length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            Live Meetings
          </h3>
          <div className="grid gap-2">
            {schedule.filter(s => s.meeting?.status === 'live').map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-lg p-3 flex items-center justify-between"
              >
                <div>
                  <h4 className="font-medium text-gray-900">{item.title}</h4>
                  {item.course && (
                    <p className="text-sm text-gray-500">{item.course.name}</p>
                  )}
                </div>
                <button
                  onClick={() => item.meeting && handleJoinMeeting(item.meeting.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <Video className="w-4 h-4" />
                  Join Now
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mini Calendar (for weekly/daily views) */}
      {(viewMode === 'weekly' || viewMode === 'daily') && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 max-w-xs">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <div className="flex gap-1">
              <button
                onClick={() => {
                  const newDate = new Date(currentDate);
                  newDate.setMonth(newDate.getMonth() - 1);
                  setCurrentDate(newDate);
                }}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  const newDate = new Date(currentDate);
                  newDate.setMonth(newDate.getMonth() + 1);
                  setCurrentDate(newDate);
                }}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs">
            {dayNames.map((d) => (
              <div key={d} className="text-gray-500 py-1">{d.charAt(0)}</div>
            ))}
            {monthDays.map((day, index) => {
              const date = day ? new Date(currentDate.getFullYear(), currentDate.getMonth(), day) : null;
              const hasEvents = day && getEventsForDay(day).length > 0;

              return (
                <button
                  key={index}
                  onClick={() => day && setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day))}
                  className={`py-1 rounded relative ${
                    date && isToday(date)
                      ? 'bg-teal-600 text-white'
                      : day === currentDate.getDate()
                      ? 'bg-teal-100 text-teal-700'
                      : day
                      ? 'hover:bg-gray-100 text-gray-700'
                      : ''
                  }`}
                >
                  {day || ''}
                  {hasEvents && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-teal-500 rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      {showCreateModal && (
        <CreateEventModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleEventCreated}
          defaultDate={currentDate}
        />
      )}
    </div>
  );
}
